/**
 * Logos du Grand Quiz du Lair : l'emblème de l'émission et un blason animé par épreuve (SVG pur).
 */
import React, { useId } from 'react';

// ——— Formes de fond ———
const poly = (n, r, rot = -90, cx = 120, cy = 104) => Array.from({ length: n }, (_, k) => {
  const a = ((rot + (360 / n) * k) * Math.PI) / 180;
  return `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`;
}).join(' ');
const star = (n, r1, r2, cx = 120, cy = 104, rot = -90) => Array.from({ length: n * 2 }, (_, k) => {
  const r = k % 2 ? r2 : r1; const a = ((rot + (180 / n) * k) * Math.PI) / 180;
  return `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`;
}).join(' ');
const SHAPES = {
  hex: (p) => <polygon points={poly(6, 84, -90)} {...p} />,
  oct: (p) => <polygon points={poly(8, 84, -67.5)} {...p} />,
  star: (p) => <polygon points={star(10, 92, 72)} {...p} />,
  burst: (p) => <polygon points={star(16, 90, 76)} {...p} />,
  circle: (p) => <circle cx="120" cy="104" r="82" {...p} />,
  shield: (p) => <path d="M120 18 L196 42 Q198 128 120 190 Q42 128 44 42 Z" {...p} />,
  diamond: (p) => <polygon points="120,14 204,104 120,194 36,104" {...p} />,
  card: (p) => <rect x="46" y="22" width="148" height="166" rx="26" {...p} />,
  pent: (p) => <polygon points={poly(5, 88, -90, 120, 110)} {...p} />
};

// ——— Icônes (centrées sur 0,0, environ ±40) ———
const W = '#ffffff'; const INK = '#0b0a2e';
const ICONS = {
  tension: () => (
    <g className="ic-bolt">
      <path d="M8 -46 L-26 6 L-4 6 L-14 46 L28 -10 L4 -10 L18 -46 Z" fill="#fff36b" stroke={INK} strokeWidth="5" strokeLinejoin="round" />
      <path className="ic-arc" d="M-40 -18 l10 6 l-6 6 l10 6" fill="none" stroke="#bff8ff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      <path className="ic-arc b" d="M40 18 l-10 -6 l6 -6 l-10 -6" fill="none" stroke="#bff8ff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  ),
  mur: () => (
    <g className="ic-wall" stroke={INK} strokeWidth="4" strokeLinejoin="round">
      {[[-40, 14, '#ff9ad9'], [0, 14, '#ffe27a'], [-20, -10, '#7cf0ff'], [20, -10, '#a3ff7a'], [-40, -34, '#ffe27a'], [0, -34, '#ff9ad9']].map(([x, y, c], k) => (
        <rect key={k} className="ic-brick" style={{ '--k': k }} x={x} y={y} width="40" height="22" rx="4" fill={c} />
      ))}
      <rect x="-60" y="14" width="20" height="22" rx="4" fill="#7cf0ff" /><rect x="40" y="14" width="20" height="22" rx="4" fill="#a3ff7a" />
    </g>
  ),
  coords: () => (
    <g>
      <circle r="42" fill="#fff6d6" stroke={INK} strokeWidth="5" />
      <circle r="33" fill="none" stroke="#2bb673" strokeWidth="3" strokeDasharray="4 6" />
      {[0, 90, 180, 270].map((a) => <path key={a} transform={`rotate(${a})`} d="M0 -40 L5 -30 L-5 -30 Z" fill={INK} />)}
      <g className="ic-needle"><path d="M0 -30 L8 0 L0 30 L-8 0 Z" fill="#ff3d6e" stroke={INK} strokeWidth="3" strokeLinejoin="round" /><path d="M0 0 L8 0 L0 30 L-8 0 Z" fill="#e8e8f0" stroke={INK} strokeWidth="3" strokeLinejoin="round" /><circle r="5" fill={INK} /></g>
    </g>
  ),
  expo: () => (
    <g stroke={INK} strokeWidth="4" strokeLinejoin="round">
      <path d="M-46 -16 L0 -44 L46 -16 Z" fill="#ffcf7a" />
      <rect x="-44" y="-16" width="88" height="8" fill="#fff1d6" />
      {[-34, -12, 10, 32].map((x) => <rect key={x} x={x - 4} y="-8" width="10" height="40" fill="#fff6e8" />)}
      <rect x="-50" y="32" width="100" height="10" rx="2" fill="#ffcf7a" />
      <g className="ic-gem"><path d="M0 -38 L7 -30 L0 -22 L-7 -30 Z" fill="#ff4d6d" strokeWidth="2.5" /></g>
    </g>
  ),
  match: () => (
    <g stroke={INK} strokeWidth="4.5" strokeLinejoin="round">
      <path className="ic-heart a" d="M-14 -22 C-14 -40 -46 -40 -46 -16 C-46 4 -24 18 -14 30 C-4 18 18 4 18 -16 C18 -40 -14 -40 -14 -22 Z" fill="#ff7ab6" />
      <path className="ic-heart b" d="M16 -12 C16 -28 44 -28 44 -6 C44 12 26 24 16 34 C6 24 -12 12 -12 -6 C-12 -28 16 -28 16 -12 Z" fill="#ffd1e8" />
      <path d="M-34 -20 q4 -8 12 -6" stroke="#fff" strokeWidth="4" fill="none" strokeLinecap="round" />
    </g>
  ),
  quiestce: ({ orbId }) => (
    <g>
      <path d="M-30 30 L30 30 L22 44 L-22 44 Z" fill="#7b2dff" stroke={INK} strokeWidth="4" strokeLinejoin="round" />
      <circle cy="-4" r="38" fill="#3a1d8f" stroke={INK} strokeWidth="5" />
      <circle className="ic-orb" cy="-4" r="30" fill={`url(#${orbId})`} />
      <g className="ic-eye"><path d="M-20 -4 Q0 -22 20 -4 Q0 14 -20 -4 Z" fill="#fff" stroke={INK} strokeWidth="3" /><circle cy="-4" r="7" fill="#22d3ee" stroke={INK} strokeWidth="2.5" /><circle cy="-4" r="3" fill={INK} /></g>
      <path d="M-22 -26 q8 -8 18 -8" stroke="#fff" strokeWidth="4" fill="none" strokeLinecap="round" opacity="0.8" />
    </g>
  ),
  blind: () => (
    <g>
      <g className="ic-vinyl"><circle r="40" fill="#141414" stroke={INK} strokeWidth="4" /><circle r="31" fill="none" stroke="#333" strokeWidth="2" /><circle r="22" fill="none" stroke="#333" strokeWidth="2" /><circle r="13" fill="#a3ff5c" stroke={INK} strokeWidth="3" /><circle r="3" fill={INK} /><path d="M-28 -18 A34 34 0 0 1 -10 -32" stroke="#fff" strokeWidth="3" fill="none" opacity="0.5" /></g>
      <path d="M-50 4 Q-50 -54 0 -54 Q50 -54 50 4" fill="none" stroke={INK} strokeWidth="11" strokeLinecap="round" />
      <path d="M-50 4 Q-50 -54 0 -54 Q50 -54 50 4" fill="none" stroke="#00b3a4" strokeWidth="6" strokeLinecap="round" />
      <rect x="-60" y="-6" width="18" height="34" rx="8" fill="#a3ff5c" stroke={INK} strokeWidth="4" /><rect x="42" y="-6" width="18" height="34" rx="8" fill="#a3ff5c" stroke={INK} strokeWidth="4" />
    </g>
  ),
  piege: () => (
    <g>
      <rect x="-40" y="-36" width="54" height="74" rx="8" transform="rotate(-14)" fill="#3b1a5a" stroke={INK} strokeWidth="4" />
      <g className="ic-card"><rect x="-22" y="-42" width="58" height="80" rx="9" transform="rotate(10)" fill="#f4ffe0" stroke={INK} strokeWidth="4.5" />
        <g transform="rotate(10) translate(7 -4)"><circle cy="-4" r="15" fill="#b6ff3b" stroke={INK} strokeWidth="3.5" /><rect x="-9" y="6" width="18" height="10" rx="2" fill="#b6ff3b" stroke={INK} strokeWidth="3.5" /><circle cx="-6" cy="-5" r="4" fill={INK} /><circle cx="6" cy="-5" r="4" fill={INK} /><path d="M-4 22 L4 30 M4 22 L-4 30" stroke={INK} strokeWidth="3" /></g></g>
    </g>
  ),
  vraifaux: () => (
    <g stroke={INK} strokeWidth="4.5" strokeLinejoin="round">
      <g className="ic-tile a"><rect x="-48" y="-30" width="46" height="54" rx="10" transform="rotate(-10)" fill="#4f7bff" /><path d="M-38 -4 l9 10 l16 -20" transform="rotate(-10)" fill="none" stroke="#fff" strokeWidth="7" strokeLinecap="round" /></g>
      <g className="ic-tile b"><rect x="2" y="-24" width="46" height="54" rx="10" transform="rotate(10)" fill="#ff6ec7" /><path d="M14 -10 l22 22 M36 -10 l-22 22" transform="rotate(10)" fill="none" stroke="#fff" strokeWidth="7" strokeLinecap="round" /></g>
    </g>
  ),
  fouilles: () => (
    <g>
      <path d="M-46 40 L-30 4 L-6 -6 L22 2 L46 40 Z" fill="#8a5a2b" stroke={INK} strokeWidth="4.5" strokeLinejoin="round" />
      <path d="M-30 4 L-14 20 L-6 -6 M22 2 L8 22 L-14 20" fill="none" stroke="#5a3613" strokeWidth="3" />
      <circle className="ic-spark" cx="4" cy="-14" r="4" fill="#fff36b" />
      <g className="ic-pick"><rect x="-4" y="-40" width="9" height="62" rx="4" transform="rotate(38 0 0)" fill="#c98a4a" stroke={INK} strokeWidth="4" /><path d="M-30 -36 Q2 -58 34 -30 L28 -24 Q2 -44 -26 -28 Z" transform="rotate(38 0 0)" fill="#d6dcf2" stroke={INK} strokeWidth="4" strokeLinejoin="round" /></g>
    </g>
  ),
  ordre: () => (
    <g stroke={INK} strokeWidth="4" strokeLinejoin="round">
      {[[-42, 18, '#ffe14d'], [-18, 2, '#ffbe3d'], [6, -16, '#ff9a2b'], [30, -34, '#ff7a00']].map(([x, h, c], k) => (
        <rect key={k} className="ic-bar" style={{ '--k': k }} x={x} y={h} width="20" height={42 - h} rx="4" fill={c} />
      ))}
      <path d="M-44 4 L-10 -18 L8 -8 L40 -46" fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round" />
      <path d="M28 -48 L44 -50 L42 -34" fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round" />
    </g>
  ),
  intrus: () => (
    <g>
      <path d="M-40 -14 Q-40 -40 0 -42 Q40 -40 40 -14 Z" fill="#3a2a1a" stroke={INK} strokeWidth="4" strokeLinejoin="round" />
      <rect x="-52" y="-18" width="104" height="10" rx="5" fill="#2a1a0a" stroke={INK} strokeWidth="4" />
      <rect x="-40" y="-26" width="80" height="8" fill="#ff3d71" />
      <g className="ic-lens"><circle cx="-4" cy="18" r="20" fill="#bff8ff" fillOpacity="0.55" stroke={INK} strokeWidth="5" /><rect x="10" y="30" width="10" height="26" rx="4" transform="rotate(-45 15 34)" fill="#ffd700" stroke={INK} strokeWidth="4" /><text x="-4" y="27" textAnchor="middle" fontSize="26" fontFamily="'Lilita One', sans-serif" fill={INK}>?</text></g>
    </g>
  ),
  mots: () => (
    <g className="ic-mic">
      <rect x="-18" y="-46" width="36" height="56" rx="18" fill="#5cf2ff" stroke={INK} strokeWidth="4.5" />
      {[-30, -20, -10, 0].map((y) => <path key={y} d={`M-17 ${y} H17`} stroke={INK} strokeWidth="2.5" opacity="0.5" />)}
      <path d="M-28 -6 Q-28 26 0 26 Q28 26 28 -6" fill="none" stroke={INK} strokeWidth="5" strokeLinecap="round" />
      <rect x="-4" y="26" width="8" height="14" fill={INK} /><rect x="-18" y="38" width="36" height="7" rx="3" fill="#ff4df0" stroke={INK} strokeWidth="3" />
      <path className="ic-note" d="M34 -36 v18 a6 6 0 1 1 -4 -5 v-17 l12 -3 v6 z" fill="#ff4df0" stroke={INK} strokeWidth="2.5" />
    </g>
  ),
  lynx: () => (
    <g>
      <path className="ic-flame" d="M0 -50 C18 -30 34 -16 28 8 C24 26 10 36 0 36 C-10 36 -24 26 -28 8 C-34 -16 -14 -24 -10 -40 C-4 -30 -2 -22 0 -16 C4 -26 4 -40 0 -50 Z" fill="#ff7a1a" stroke={INK} strokeWidth="4" strokeLinejoin="round" />
      <g className="ic-lid"><path d="M-36 6 Q0 -24 36 6 Q0 34 -36 6 Z" fill="#fff6d6" stroke={INK} strokeWidth="4.5" /><ellipse cy="6" rx="10" ry="14" fill="#ffd34d" stroke={INK} strokeWidth="3" /><ellipse cy="6" rx="3.5" ry="11" fill={INK} /></g>
    </g>
  ),
  labo: () => (
    <g>
      <path d="M-12 -46 H12 V-14 L36 34 Q40 44 28 44 H-28 Q-40 44 -36 34 L-12 -14 Z" fill="#e6fff6" fillOpacity="0.9" stroke={INK} strokeWidth="4.5" strokeLinejoin="round" />
      <path d="M-25 14 H25 L34 34 Q37 40 28 40 H-28 Q-37 40 -34 34 Z" fill="#4dffb8" />
      <rect x="-16" y="-52" width="32" height="9" rx="4" fill="#2563ff" stroke={INK} strokeWidth="4" />
      {[[-8, 26, 5], [10, 20, 4], [2, 6, 3]].map(([x, y, r], k) => <circle key={k} className="ic-bubble" style={{ '--k': k }} cx={x} cy={y} r={r} fill="#fff" />)}
    </g>
  )
};

export const LOGO_TEXT = {
  tension: ['Quiz sous', 'Haute Tension'], mur: ['Le Mur des', 'Connexions'], coords: ['Coordonnées', 'Perdues'], expo: ['L’Exposition du', 'Musée'],
  match: ['Le Bon', 'Match'], quiestce: ['Lecture', 'Mentale'], blind: ['La Mixtape', 'du DJ'], piege: ['Carte', 'Piège'], vraifaux: ['Vrai', 'ou Faux'],
  fouilles: ['Les Fouilles', 'Interdites'], ordre: ['Stats', 'dans l’Ordre'], intrus: ['de la Bande', 'L’Intrus', 1], mots: ['Les Mots', 'Manquants'], lynx: ['Œil de', 'Lynx'], labo: ['des Énigmes', 'Le Labo', 1]
};
const SHAPE_OF = { tension: 'star', mur: 'card', coords: 'circle', expo: 'pent', match: 'burst', quiestce: 'oct', blind: 'circle', piege: 'diamond', vraifaux: 'hex', fouilles: 'shield', ordre: 'hex', intrus: 'oct', mots: 'burst', lynx: 'shield', labo: 'hex' };

const fit = (txt, max, width) => Math.min(max, (width / Math.max(4, txt.length)) * 1.62);

/** Blason animé d'une épreuve. */
export function Emblem({ f, size, still }) {
  const [small, big, bottom] = LOGO_TEXT[f.id] || [f.name[0], f.name[1]];
  const Shape = SHAPES[SHAPE_OF[f.id] || 'hex'];
  const Icon = ICONS[f.id] || (() => null);
  const uid = useId().replace(/:/g, '');
  const g = `qsg-${f.id}-${uid}`; const t = `qst-${f.id}-${uid}`; const orb = `qsOrb-${uid}`;
  const bigUp = big.toUpperCase(); const smallUp = small.toUpperCase();
  const bs = fit(bigUp, 46, 224); const ss = fit(smallUp, 17, 150);
  const bigY = bottom ? 186 : 206; const smallY = bottom ? 218 : 160;
  const tagW = Math.min(196, smallUp.length * ss * 0.68 + 28);
  return (
    <svg className={`qs-emblem${still ? ' is-still' : ''}`} viewBox="0 0 240 240" width={size} height={size} role="img" aria-label={`${small} ${big}`}>
      <defs>
        <linearGradient id={g} x1="0" y1="0" x2="0.4" y2="1"><stop offset="0" stopColor={f.c1} /><stop offset="1" stopColor={f.c2} /></linearGradient>
        <linearGradient id={t} x1="0" y1="0" x2="0" y2="1"><stop offset="0.1" stopColor="#ffffff" /><stop offset="0.55" stopColor={f.c1} /><stop offset="1" stopColor={f.c2} /></linearGradient>
        <radialGradient id={orb} cx="0.4" cy="0.35"><stop offset="0" stopColor="#f0d9ff" /><stop offset="0.5" stopColor="#c084fc" /><stop offset="1" stopColor="#22d3ee" /></radialGradient>
        <clipPath id={`${g}-c`}>{Shape({})}</clipPath>
      </defs>
      <g className="em-badge">
        {Shape({ fill: '#090726', transform: 'translate(0 7)' })}
        {Shape({ fill: `url(#${g})`, stroke: '#090726', strokeWidth: 8, strokeLinejoin: 'round' })}
        <g clipPath={`url(#${g}-c)`}>
          <g className="em-rays" style={{ transformOrigin: '120px 104px' }}>{Array.from({ length: 12 }, (_, k) => <path key={k} d="M120 104 L112 -40 L128 -40 Z" transform={`rotate(${k * 30} 120 104)`} fill="#fff" opacity="0.09" />)}</g>
          <ellipse cx="120" cy="50" rx="110" ry="46" fill="#fff" opacity="0.14" />
          <path className="em-shine" d="M-20 -20 h34 l-90 290 h-34 z" fill="#fff" opacity="0.35" />
        </g>
        {Shape({ fill: 'none', stroke: '#fff', strokeOpacity: 0.55, strokeWidth: 3, transform: 'translate(120 104) scale(0.9) translate(-120 -104)' })}
      </g>
      <g className="em-icon" transform="translate(120 92)"><g className="em-icon-in"><Icon orbId={orb} /></g></g>
      <g className="em-title" fontFamily="'Lilita One', sans-serif" textAnchor="middle">
        <g className="em-tag">
          <rect x={120 - tagW / 2} y={smallY - ss - 1} width={tagW} height={ss + 11} rx={(ss + 11) / 2} fill="#090726" />
          <rect x={120 - tagW / 2 + 3} y={smallY - ss + 2} width={tagW - 6} height={ss + 5} rx={(ss + 5) / 2} fill="none" stroke={f.c1} strokeWidth="2" />
          <text x="120" y={smallY + 1} fontSize={ss} fill="#fff" letterSpacing="1">{smallUp}</text>
        </g>
        <text x="120" y={bigY + 5} fontSize={bs} fill="#090726" stroke="#090726" strokeWidth="11" strokeLinejoin="round">{bigUp}</text>
        <text x="120" y={bigY} fontSize={bs} fill={`url(#${t})`} stroke="#090726" strokeWidth="9" paintOrder="stroke" strokeLinejoin="round">{bigUp}</text>
        <text x="120" y={bigY} fontSize={bs} fill="none" stroke="#fff" strokeOpacity="0.5" strokeWidth="1.2">{bigUp}</text>
      </g>
    </svg>
  );
}

/** Emblème de l'émission : « Le Grand Quiz du Lair ». */
export function ShowLogo({ size = 420, still }) {
  const u = useId().replace(/:/g, '');
  const id = (n) => `${n}-${u}`;
  return (
    <svg className={`qs-showlogo${still ? ' is-still' : ''}`} viewBox="0 0 520 360" width={size} height={size * 0.69} role="img" aria-label="Le Grand Quiz du Lair">
      <defs>
        <linearGradient id={id('slGold')} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#fffbe0" /><stop offset="0.35" stopColor="#ffd84d" /><stop offset="0.7" stopColor="#ff9a1a" /><stop offset="1" stopColor="#d65a00" /></linearGradient>
        <linearGradient id={id('slPlate')} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#6a3dff" /><stop offset="0.55" stopColor="#3b1fb0" /><stop offset="1" stopColor="#ff3da0" /></linearGradient>
        <linearGradient id={id('slRibbon')} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#ff5c8a" /><stop offset="1" stopColor="#b8104a" /></linearGradient>
        <radialGradient id={id('slGlow')}><stop offset="0" stopColor="#ffd84d" stopOpacity="0.7" /><stop offset="1" stopColor="#ffd84d" stopOpacity="0" /></radialGradient>
        <path id={id('slArc')} d="M110 150 Q260 40 410 150" />
        <clipPath id={id('slPlateClip')}><path d="M260 36 L452 112 L420 268 L260 330 L100 268 L68 112 Z" /></clipPath>
      </defs>
      <circle cx="260" cy="178" r="190" fill={`url(#${id('slGlow')})`} className="sl-glow" />
      <g className="sl-rays" style={{ transformOrigin: '260px 178px' }}>
        {Array.from({ length: 24 }, (_, k) => <path key={k} d="M260 178 L248 -60 L272 -60 Z" transform={`rotate(${k * 15} 260 178)`} fill={k % 2 ? '#ffd84d' : '#ff5cc8'} opacity="0.16" />)}
      </g>
      <g className="sl-plate">
        <path d="M260 46 L452 122 L420 278 L260 340 L100 278 L68 122 Z" fill="#090726" />
        <path d="M260 36 L452 112 L420 268 L260 330 L100 268 L68 112 Z" fill={`url(#${id('slPlate')})`} stroke="#090726" strokeWidth="10" strokeLinejoin="round" />
        <g clipPath={`url(#${id('slPlateClip')})`}>
          <path d="M60 60 L460 60 L460 150 Q260 110 60 150 Z" fill="#fff" opacity="0.1" />
          <path className="sl-shine" d="M-40 0 h60 l-140 400 h-60 z" fill="#fff" opacity="0.28" />
          {Array.from({ length: 7 }, (_, k) => <circle key={k} cx={110 + k * 50} cy={300 - (k % 3) * 8} r="3" fill="#fff" opacity="0.5" />)}
        </g>
        <path d="M260 52 L436 122 L407 258 L260 314 L113 258 L84 122 Z" fill="none" stroke="#ffd84d" strokeWidth="4" strokeOpacity="0.85" />
      </g>
      <g transform="translate(260 40)"><g className="sl-crown">
        <path d="M-44 14 L-50 -26 L-24 -6 L0 -40 L24 -6 L50 -26 L44 14 Z" fill={`url(#${id('slGold')})`} stroke="#090726" strokeWidth="7" strokeLinejoin="round" />
        <circle cx="0" cy="-40" r="7" fill="#ff5c8a" stroke="#090726" strokeWidth="4" /><circle cx="-50" cy="-26" r="6" fill="#5cf2ff" stroke="#090726" strokeWidth="4" /><circle cx="50" cy="-26" r="6" fill="#5cf2ff" stroke="#090726" strokeWidth="4" />
      </g></g>
      <text fontFamily="'Lilita One', sans-serif" fontSize="38" letterSpacing="4" fill="#fff" stroke="#090726" strokeWidth="8" paintOrder="stroke" strokeLinejoin="round" textAnchor="middle"><textPath href={`#${id('slArc')}`} startOffset="50%">LE GRAND QUIZ</textPath></text>
      <g className="sl-title" fontFamily="'Lilita One', sans-serif" textAnchor="middle">
        <text x="260" y="246" fontSize="112" fill="#090726" stroke="#090726" strokeWidth="18" strokeLinejoin="round">DU LAIR</text>
        <text x="260" y="236" fontSize="112" fill={`url(#${id('slGold')})`} stroke="#090726" strokeWidth="14" paintOrder="stroke" strokeLinejoin="round">DU LAIR</text>
        <text x="260" y="236" fontSize="112" fill="none" stroke="#fff8d0" strokeOpacity="0.7" strokeWidth="2">DU LAIR</text>
      </g>
      <g className="sl-ribbon">
        <path d="M120 266 L400 266 L420 286 L400 306 L120 306 L100 286 Z" fill="#7a0a30" transform="translate(0 6)" />
        <path d="M120 266 L400 266 L420 286 L400 306 L120 306 L100 286 Z" fill={`url(#${id('slRibbon')})`} stroke="#090726" strokeWidth="6" strokeLinejoin="round" />
        <text x="260" y="294" fontFamily="'Saira', sans-serif" fontStyle="italic" fontWeight="900" fontSize="22" letterSpacing="3" fill="#fff" textAnchor="middle">15 ÉPREUVES · 3 JOKERS</text>
      </g>
      {[[70, 70, 1], [462, 92, 0.8], [440, 300, 1.1], [86, 300, 0.7], [380, 30, 0.6], [150, 24, 0.9]].map(([x, y, s], k) => (
        <path key={k} className="sl-spark" style={{ '--k': k }} transform={`translate(${x} ${y}) scale(${s})`} d="M0 -16 L4 -4 L16 0 L4 4 L0 16 L-4 4 L-16 0 L-4 -4 Z" fill="#fff6c2" />
      ))}
    </svg>
  );
}
